import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Flame, LogIn } from "lucide-react";
import { useAuth } from "@/lib/auth";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/09a62dc9-6a9e-4bad-b528-41b496ab2382/images/cbcacb73a9857c56eaa830e6342d9e2d9c033cf8fd7db1c24131862abae98730.png";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="login-page">
      <Card className="w-full max-w-sm border-zinc-800 bg-zinc-900 rounded-sm shadow-2xl">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Superflame" className="h-14 mx-auto mb-4" />
            <h1 className="font-heading font-black text-2xl text-white tracking-tight">
              SUPERFLAME
            </h1>
            <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-[0.25em] mt-1">
              Gas Billing System
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-400 text-xs">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@superflame.com"
                required
                className="bg-zinc-800 border-zinc-700 text-white rounded-none mt-1 placeholder:text-zinc-600"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="bg-zinc-800 border-zinc-700 text-white rounded-none mt-1 placeholder:text-zinc-600"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-400 bg-red-950/30 border border-red-900/50 p-2">
                {error}
              </p>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-sm mt-2"
            >
              {loading ? "Signing in..." : (
                <><LogIn className="w-4 h-4 mr-2" /> Sign In</>
              )}
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-2 text-zinc-600">
            <Flame className="w-3 h-3 text-orange-600" />
            <span className="text-[10px] font-mono">SUPERFLAME v1.0</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
